import React, { useEffect, useRef } from 'react'
import type { Priority } from '../../redux/tasks'
import styles from './PriorityPopover.module.css'

const PRIORITY_OPTIONS: Array<{ value: Priority; label: string; description: string }> = [
  { value: 'p1', label: 'P1', description: 'Urgent' },
  { value: 'p2', label: 'P2', description: 'High' },
  { value: 'p3', label: 'P3', description: 'Medium' },
  { value: 'p4', label: 'P4', description: 'Low' },
]

const priorityDotClass = (priority: Priority | undefined): string => {
  switch (priority) {
    case 'p1':
      return styles.dotP1
    case 'p2':
      return styles.dotP2
    case 'p3':
      return styles.dotP3
    case 'p4':
      return styles.dotP4
    default:
      return ''
  }
}

export type PriorityPopoverProps = {
  current: Priority | undefined
  onPick: (priority: Priority | undefined) => void
  onClose: () => void
  align?: 'left' | 'right'
  allowNone?: boolean
}

const PriorityPopover = ({
  current,
  onPick,
  onClose,
  align = 'left',
  allowNone = true,
}: PriorityPopoverProps) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', onDoc)
    return () => window.removeEventListener('mousedown', onDoc)
  }, [onClose])

  return (
    <div
      ref={ref}
      className={`${styles.popover} ${align === 'right' ? styles.alignRight : styles.alignLeft}`}
      role="menu"
      aria-label="Set priority"
    >
      {PRIORITY_OPTIONS.map((opt) => {
        const selected = current === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="menuitemradio"
            aria-checked={selected}
            className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
            onClick={() => onPick(opt.value)}
          >
            <span className={`${styles.dot} ${priorityDotClass(opt.value)}`} aria-hidden />
            <span className={styles.label}>{opt.label}</span>
            <span className={styles.description}>{opt.description}</span>
          </button>
        )
      })}
      {allowNone ? (
        <button
          type="button"
          role="menuitemradio"
          aria-checked={current === undefined}
          className={`${styles.option} ${
            current === undefined ? styles.optionSelected : ''
          }`}
          onClick={() => onPick(undefined)}
        >
          <span className={`${styles.dot} ${styles.dotNone}`} aria-hidden />
          <span className={styles.label}>None</span>
          <span className={styles.description}>No priority</span>
        </button>
      ) : null}
    </div>
  )
}

export default PriorityPopover
